import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";
import { Printer, ArrowLeft, MessageCircle } from "lucide-react";
import { buildGuestWhatsAppUrl } from "@/lib/whatsapp";

const STATUS_LABEL = {
  pending: "Pending",
  confirmed: "Confirmed",
  declined: "Declined",
  cancelled: "Cancelled",
};

export default function BookingConfirmation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff/reservations");
        const found = data.find((r) => r.id === id);
        if (!found) setErr("Reservation not found.");
        else setRes(found);
      } catch (e) {
        setErr("Could not load reservation.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const cancelUrl = res ? `${origin}/r/cancel/${res.cancel_token}` : "";
  const confirmCode = res ? (res.cancel_token || res.id).slice(0, 8).toUpperCase() : "";
  const waUrl = res ? buildGuestWhatsAppUrl(res, origin) : null;

  if (loading) return <div className="pt-32 pb-24 max-w-3xl mx-auto px-6" data-testid="booking-loading">Loading...</div>;
  if (err) return (
    <div className="pt-32 pb-24 max-w-3xl mx-auto px-6" data-testid="booking-error">
      <div className="eyebrow">Error</div>
      <h1 className="mt-2 text-3xl">{err}</h1>
      <button onClick={() => nav("/staff/reservations")} className="btn-brass mt-6"><ArrowLeft size={14}/> Back to reservations</button>
    </div>
  );

  return (
    <div data-testid="booking-print-page" className="bg-[hsl(var(--bg))] min-h-screen">
      {/* Controls — hidden on print */}
      <div className="print:hidden bg-[hsl(var(--forest-2))] text-[hsl(var(--bg))] py-4">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => nav("/staff/reservations")} data-testid="booking-back" className="inline-flex items-center gap-1.5 text-sm hover:text-[hsl(var(--brass))]">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="text-sm">
            Confirmation for <span className="text-[hsl(var(--brass))]">{res.name}</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {waUrl && (
              <a
                data-testid="booking-whatsapp"
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass))]"
              >
                <MessageCircle size={12} /> WhatsApp
              </a>
            )}
            <button
              data-testid="booking-print-btn"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded bg-[hsl(var(--brass))] text-[hsl(var(--forest-2))] font-semibold"
            >
              <Printer size={12} /> Print
            </button>
          </div>
        </div>
      </div>

      {/* Print sheet */}
      <div className="max-w-3xl mx-auto px-6 py-10 print:p-0 print:max-w-none">
        <section data-testid="booking-print-sheet" className="bg-white text-black booking-sheet">
          {/* Header */}
          <header className="pb-5 border-b-2 border-black flex items-start justify-between gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-600">Booking Confirmation</div>
              <div className="mt-2 text-4xl font-serif tracking-tight">
                The Barracks <span className="italic">Bistro</span>
              </div>
              <div className="mt-1 text-xs text-neutral-600 italic">Wellington · The Nilgiris</div>
            </div>
            <div className="text-right text-[11px] leading-snug">
              <div>{SITE.address}</div>
              <div className="mt-1">{SITE.phonePrimary}</div>
              <div>{SITE.phoneSecondary}</div>
              <div>{SITE.email}</div>
            </div>
          </header>

          {/* Confirmation number band */}
          <div className="mt-5 flex items-baseline justify-between border border-black p-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Confirmation No.</div>
              <div className="mt-1 text-2xl font-mono tracking-widest" data-testid="booking-code">{confirmCode}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Status</div>
              <div className="mt-1 text-lg font-semibold uppercase" data-testid="booking-status">
                {STATUS_LABEL[res.status] || res.status}
              </div>
            </div>
          </div>

          {/* Body: guest + booking + qr */}
          <div className="mt-6 grid grid-cols-3 gap-6">
            <div className="col-span-2 grid grid-cols-2 gap-y-4 gap-x-6">
              <Field k="Guest" v={res.name} />
              <Field k="Party size" v={`${res.party_size} pax`} />
              <Field k="Date" v={res.date} />
              <Field k="Time" v={res.time} />
              <Field k="Phone" v={res.phone} />
              <Field k="Email" v={res.email} />
              {res.occasion && <Field k="Occasion" v={res.occasion} span2 />}
              {res.notes && <Field k="Notes" v={res.notes} span2 />}
            </div>

            <div className="col-span-1 flex flex-col items-center justify-start">
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-600 text-center">Manage booking</div>
              <div className="mt-2 border border-black p-2" data-testid="booking-qr">
                <QRCodeSVG value={cancelUrl} size={128} bgColor="#ffffff" fgColor="#000000" level="M" />
              </div>
              <div className="mt-2 text-[9px] text-center text-neutral-600 max-w-[130px] break-words">
                Scan to view or cancel
              </div>
            </div>
          </div>

          {/* Manage URL */}
          <div className="mt-6 pt-4 border-t border-neutral-400">
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Manage / cancel online</div>
            <div className="mt-1 text-xs font-mono break-all" data-testid="booking-cancel-url">{cancelUrl}</div>
          </div>

          {/* Policies */}
          <div className="mt-6 grid grid-cols-2 gap-6 text-[11px] leading-relaxed">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold">Table policy</div>
              <p className="mt-1 text-neutral-700">
                We hold your table for 20 minutes past the reserved time. Kindly WhatsApp us on{" "}
                {SITE.phonePrimary} if you're running late. The kitchen closes 30 minutes before we do.
              </p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold">Cancellation</div>
              <p className="mt-1 text-neutral-700">
                Cancel up to 4 hours before your reservation, at no charge, via the QR code above or the
                link on this sheet. Same-day cancellations may incur a nominal fee for events.
              </p>
            </div>
          </div>

          {/* Footer signature */}
          <footer className="mt-8 pt-4 border-t border-black grid grid-cols-3 text-[10px] uppercase tracking-widest text-neutral-700">
            <div>Received by: ______________</div>
            <div>Manager: __________________</div>
            <div className="text-right">Printed: {new Date().toLocaleString("en-IN")}</div>
          </footer>
        </section>

        <div className="print:hidden mt-6 text-center text-xs text-[hsl(var(--ink-soft))]">
          <Link to="/staff/reservations" className="text-[hsl(var(--forest))] font-semibold underline underline-offset-4">
            ← Back to reservations
          </Link>
        </div>
      </div>

      <style>{`
        .booking-sheet {
          padding: 2.25rem;
          border: 1px solid hsl(var(--rule));
          box-shadow: 0 20px 60px -30px hsl(0 0% 0% / 0.35);
          font-family: Georgia, "Times New Roman", serif;
        }
        @media print {
          body, html { background: white !important; }
          .booking-sheet {
            border: none;
            padding: 0;
            box-shadow: none;
          }
          @page { size: A4; margin: 15mm; }
        }
      `}</style>
    </div>
  );
}

function Field({ k, v, span2 }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-600">{k}</div>
      <div className="mt-0.5 text-sm">{v || "—"}</div>
    </div>
  );
}
